import { defineStore } from "pinia";
import { createClient } from "@/sdk";
import {
  type WebhusetOrderApiFeaturesCartItemDomainAddRequest,
  type WebhusetOrderApiFeaturesSharedModelsCartItem,
  type WebhusetOrderApiFeaturesCartItemAddOnAddRequest,
  type WebhusetOrderApiFeaturesCartItemVpsAddRequest,
  type WebhusetOrderApiFeaturesCartItemVpsAddResponse,
  type WebhusetOrderApiFeaturesCartQuoteResponseWithDomainDiscountExceeded,
  type WebhusetOrderApiFeaturesCartItemMicrosoft365AddRequest,
  type WebhusetOrderApiFeaturesCartItemMicrosoft365AddResponse,
  type WebhusetOrderApiFeaturesCartItemMicrosoft365PutRequest,
  WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItemObject,
  type WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItem,
  type WebhusetOrderApiDataCustomerDeviceTypeItem,
  type WebhusetOrderApiDataOrderPaymentMethodTypeItem,
  serializeWebhusetOrderApiFeaturesCartOrderPrepareExtendedOrderAddRestDto,
} from "@/sdk/generated/models";
import {
  DefaultApiError,
  SerializationWriterFactoryRegistry,
  type ApiError,
} from "@microsoft/kiota-abstractions";
import { useMicrosoftCloudAgreementStore } from "./microsoft-cloud-agreement";
import {
  getUtmCampaign,
  getUtmTerm,
  hasAdwordsCookie,
} from "~/helpers/cookieHelper";
import { useDevice } from "#imports";
import type { OrderAddResult } from "~/types/api/order";
import type { WebhusetApiError } from "~/models";
import { JsonSerializationWriterFactory } from "@microsoft/kiota-serialization-json";

export const useCartStore = defineStore(
  "cart",
  () => {
    const toast = useToast();
    const config = useRuntimeConfig();
    const router = useRouter();
    const { restPost } = useWebhusetApi();

    const client = createClient(config.public.apiServer);

    const authStore = useAuthStore();
    const microsoftCloudAgreementStore = useMicrosoftCloudAgreementStore();

    const initialized = ref(false);
    const error = ref(false);

    const hasAcceptedTerms = ref(false);
    const hasAcceptedPrivacy = ref(false);

    const selectedPaymentMethod =
      ref<WebhusetOrderApiDataOrderPaymentMethodTypeItem>();

    const domainDiscountQuotaExceeded = ref(false);
    const domainDiscountQuotaExceededDetails =
      ref<WebhusetOrderApiFeaturesCartQuoteResponseWithDomainDiscountExceeded>();

    const hasSeenDomainAddonsPage = ref(false);

    const cartId = ref<string>();
    const domainSubscriptionPaymentInterval =
      ref<WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItem>(
        WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItemObject.Annually
      );

    // Cheap way to lock the cart while we're doing stuff
    let locked = false;

    const items = ref<WebhusetOrderApiFeaturesSharedModelsCartItem[]>([]);
    const coupon = ref<string>();

    const init = async (couponFromUrl?: string) => {
      if (initialized.value) {
        return;
      }

      try {
        if (cartId.value) {
          const cart = await client.cart.byCartId(cartId.value).get();

          if (!cart?.items) {
            throw new Error("Cart returned invalid data");
          }

          if (cart.isFinalized) {
            console.log("Cart is finalized, resetting cart id");
            cartId.value = undefined;
          } else {
            items.value = cart.items;
            domainSubscriptionPaymentInterval.value =
              cart.domainSubscriptionPaymentInterval ??
              WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItemObject.Annually;
            coupon.value = cart.coupon ?? "DEFAULT";

            if (couponFromUrl && couponFromUrl !== coupon.value) {
              await trySetCoupon(couponFromUrl);
            }
          }
        } else {
          coupon.value = couponFromUrl ?? config.public.defaultCoupon;
          console.log("Coupon set to", coupon.value, "for non-persisted cart");
        }

        initialized.value = true;
      } catch (e) {
        if (e instanceof DefaultApiError && e.responseStatusCode === 404) {
          console.log("Cart not found, resetting cart id");
          cartId.value = undefined;
          initialized.value = true;
          return;
        }

        console.log("error in cart init", e);
        error.value = true;
        showError({
          statusCode: 500,
          name: "En feil oppstod",
          statusMessage: "Kunne ikke hente handlekurv",
          fatal: true,
        });
      }
    };

    const itemCount = computed(() => items.value.length);

    const microsoft365 = computed(() =>
      items.value.filter((item) => item.microsoft365?.subscriptionId)
    );

    watch(microsoft365, () => {
      microsoftCloudAgreementStore.setAgreementRequired(
        microsoft365.value.length > 0
      );
    });

    watch(
      [() => authStore.customerId, () => authStore.partnerId, cartId],
      async () => {
        if (authStore.isAuthenticated && cartId.value) {
          try {
            await client.cart.byCartId(cartId.value).linkToCustomer.put({
              customerId: authStore.customerId,
              partnerId: authStore.partnerId,
            });
          } catch (e) {
            console.error("Failed to link cart to customer", e);
          }
        }
      }
    );

    const addDomainToCart = async (
      domain: WebhusetOrderApiFeaturesCartItemDomainAddRequest
    ) => {
      await addToCart(async () => {
        const res = await client.cart
          .byCartId(cartId.value!)
          .domain.post(domain);

        if (!res?.item?.id) {
          throw new Error("Failed to add item to cart");
        }

        items.value.push(res.item);

        if (items.value.filter((i) => i.domain?.fqdn).length === 1) {
          // Workaround for https://github.com/nuxt/nuxt/issues/28033
          for (const addonId of [
            ...new Set(config.public.defaultDomainAddons),
          ]) {
            await addAddOnToCart(res.item.id, {
              addOnId: addonId,
            });
          }
        }
      });
    };

    const addVpsToCart = async (
      vps: WebhusetOrderApiFeaturesCartItemVpsAddRequest
    ) => {
      return await addToCart<WebhusetOrderApiFeaturesCartItemVpsAddResponse>(
        async () => {
          console.log("adding to cart", vps);
          const res = await client.cart.byCartId(cartId.value!).vps.post(vps);

          if (!res?.item) {
            throw new Error("Failed to add item to cart");
          }

          items.value.push(res.item);

          return res;
        }
      );
    };

    const addMicrosoft365ToCart = async (
      microsoft365: WebhusetOrderApiFeaturesCartItemMicrosoft365AddRequest
    ) => {
      return await addToCart<WebhusetOrderApiFeaturesCartItemMicrosoft365AddResponse>(
        async () => {
          console.log("adding to cart", microsoft365);
          const res = await client.cart
            .byCartId(cartId.value!)
            .microsoft365.post(microsoft365);

          if (!res?.item?.id) {
            throw new Error("Failed to add item to cart");
          }

          items.value.push(res.item);

          return res;
        }
      );
    };

    const updateMicrosoft365Item = async (
      cartItemId: string,
      microsoft365: WebhusetOrderApiFeaturesCartItemMicrosoft365PutRequest
    ) => {
      try {
        const res = await client.cart
          .byCartId(cartId.value!)
          .microsoft365.byCartItemId(cartItemId)
          .put(microsoft365);

        if (!res?.item?.id) {
          throw new Error("Failed to update Microsoft 365 item in cart");
        }

        const index = items.value.findIndex((i) => i.id === cartItemId);
        if (index > -1) {
          items.value[index] = res.item;
        }

        toast.add({
          id: "cart-microsoft-365-update-success",
          icon: "i-heroicons-check-circle",
          title: "Endringene ble lagret",
          color: "green",
        });

        return res;
      } catch (err) {
        console.warn("Error updating Microsoft 365 cart item", err);
        toast.add({
          id: "cart-microsoft-365-update-error",
          icon: "i-heroicons-exclamation-triangle",
          title: "En feil oppstod",
          description:
            "Kunne ikke oppdatere handlekurven. Vennligst forsøk igjen.",
          color: "red",
        });
        throw err;
      }
    };

    const addAddOnToCart = async (
      parentItemId: string,
      addOn: WebhusetOrderApiFeaturesCartItemAddOnAddRequest
    ) => {
      await addToCart(async () => {
        console.log("adding to cart", addOn);
        const res = await client.cart
          .byCartId(cartId.value!)
          .item.byCartItemId(parentItemId)
          .addon.post(addOn);

        if (!res?.item) {
          throw new Error("Failed to add item to cart");
        }

        if (addOn.replacesAddOnIds?.length) {
          console.log("checking for items to remove");
          items.value = items.value.filter((item) => {
            if (
              item.parentItemId === parentItemId &&
              item.addOn?.addOnId &&
              addOn.replacesAddOnIds!.includes(item.addOn.addOnId)
            ) {
              // Addon is replaced, remove it from the cart
              console.log("removed item id", item.id);
              return false;
            }

            return true;
          });
        }
        items.value.push(res.item);
      });
    };

    const addToCart = async <T>(apiRequest: () => Promise<T>) => {
      try {
        if (locked) {
          throw new Error("Cart is locked");
        }

        if (!cartId.value) {
          locked = true;

          // When adding our first item to the cart, we need to create the cart first
          try {
            const { isMobile, isTablet } = useDevice();

            let device: WebhusetOrderApiDataCustomerDeviceTypeItem = "Desktop";

            if (isMobile) {
              device = "Mobile";
            } else if (isTablet) {
              device = "Tablet";
            }

            const cart = await client.cart.post({
              companyProfileId: config.public.companyProfileId,
              deviceType: device,
              isPpc: hasAdwordsCookie(),
              utmCampaign: getUtmCampaign(),
              utmTerm: getUtmTerm(),
            });

            if (!cart?.id) {
              throw new Error("Failed to create cart");
            }

            console.log("Created cart", cart.id);
            cartId.value = cart.id;

            if (coupon.value) {
              await trySetCoupon(coupon.value);
            }
          } catch (e) {
            console.error(e, "Failed to create cart");

            throw e;
          } finally {
            locked = false;
          }
        }

        return await apiRequest();
      } catch (err) {
        console.warn(err);
        toast.add({
          id: "cart-add-error",
          icon: "i-heroicons-exclamation-triangle",
          title: "En feil oppstod",
          description:
            "Kunne ikke legge varen i handlekurven. Vennligst forsøk igjen.",
          color: "red",
        });
        throw err;
      }
    };

    const removeFromCart = async (itemId: string) => {
      try {
        if (locked) {
          throw new Error("Cart is locked");
        }

        if (!cartId.value) {
          throw new Error("No cart id found");
        }

        const res = await client.cart
          .byCartId(cartId.value)
          .item.byCartItemId(itemId)
          .delete();

        if (!res?.success) {
          throw new Error("Failed to remove cart item");
        }

        items.value = [
          ...items.value.filter(
            (item) => !(item.id === itemId || item.parentItemId === itemId)
          ),
        ];

        toast.add({
          id: "cart-item-removed",
          icon: "i-heroicons-trash",
          title: "Fjernet fra handlekurv",
          description: "Varen ble fjernet fra handlekurven.",
          color: "green",
          // actions: [
          //   {
          //     label: "Angre",
          //     click: () => alert("Action 1 clicked!"),
          //   },
          // ],
        });
      } catch (err) {
        console.warn(err);
        toast.add({
          id: "cart-remove-error",
          icon: "i-heroicons-exclamation-triangle",
          title: "En feil oppstod",
          description:
            "Kunne ikke fjerne varen fra handlekurven. Vennligst forsøk igjen.",
          color: "red",
        });
        throw err;
      }
    };

    const clearErrors = () => {
      domainDiscountQuotaExceeded.value = false;
      domainDiscountQuotaExceededDetails.value = undefined;
    };

    const trySetCoupon = async (newCoupon: string) => {
      try {
        if (!cartId.value) {
          throw new Error("No cart id found");
        }

        const res = await client.cart.byCartId(cartId.value).coupon.put({
          coupon: newCoupon,
        });

        if (res?.success === true) {
          coupon.value = newCoupon;

          clearErrors();
          return true;
        }

        if (!res?.success) {
          throw new Error("Failed to set coupon");
        }
        return false;
      } catch (err) {
        console.warn(err);
        toast.add({
          id: "cart-coupon-error",
          icon: "i-heroicons-exclamation-triangle",
          title: "En feil oppstod",
          description:
            "Kunne ikke legge til kupongkoden. Vennligst forsøk igjen.",
          color: "red",
        });
        return false;
      }
    };

    const tryUpdateDomainSubscriptionPaymentInterval = async (
      paymentInterval: WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItem
    ) => {
      try {
        if (!cartId.value) {
          throw new Error("No cart id found");
        }

        const res = await client.cart
          .byCartId(cartId.value)
          .domainSubscriptionPaymentInterval.put({
            domainSubscriptionPaymentInterval: paymentInterval,
          });

        if (res?.success === true) {
          domainSubscriptionPaymentInterval.value = paymentInterval;

          return true;
        }

        throw new Error("Failed to set DomainSubscriptionPaymentInterval");
      } catch (err) {
        console.warn(err);
        toast.add({
          id: "cart-domain-subscription-payment-interval-error",
          icon: "i-heroicons-exclamation-triangle",
          title: "En feil oppstod",
          description:
            "Kunne ikke oppdatere valg av betalingsperiode. Vennligst forsøk igjen.",
          color: "red",
        });
        return false;
      }
    };

    const getQuote = async () => {
      if (!cartId.value) {
        throw new Error("No cart id found");
      }

      if (!authStore.apiKey) {
        throw new Error("No API key found");
      }

      clearErrors();

      try {
        return await client.cart.byCartId(cartId.value).quote.post({
          apiKey: authStore.apiKey,
          impersonateCustomerId: authStore.isPartner
            ? authStore.customerId
            : undefined,
        });
      } catch (err) {
        const statusCode = (err as ApiError)?.responseStatusCode;

        if (statusCode === 401) {
          console.log("got 401, logging out");
          authStore.logout(true);
        } else if (statusCode === 412) {
          console.log("Domain discount quota exceeded");
          const errorDetails =
            err as WebhusetOrderApiFeaturesCartQuoteResponseWithDomainDiscountExceeded;

          domainDiscountQuotaExceeded.value = true;
          domainDiscountQuotaExceededDetails.value = errorDetails;

          return errorDetails;
        } else {
          console.log("got error", err);
        }

        throw err;
      }
    };

    const placeOrder = async () => {
      try {
        console.log("preparing order");
        const route = router.resolve("/kvittering");
        const absoluteUrl = new URL(route.href, window.location.origin).href;

        const prepared = await client.cart
          .byCartId(cartId.value!)
          .orderPrepare.post({
            smsVerificationTransactionId: authStore.codeTransactionId,
            paymentMethod: selectedPaymentMethod.value || "Invoice",
            returnUrl: absoluteUrl,
          });

        if (!prepared?.orderData) {
          throw new Error("Failed to prepare order");
        }

        const { orderData: orderDataTemp } = prepared;

        if (!authStore.customerId) {
          throw new Error("No customer id found");
        }

        // orderData uses camelCase, but the API expects snake_case. Need to convert it.
        // Normally the generated SDK handles this automatically, but because we use the generic `restPost` for POST /checkout, this doesn't happen.
        // Tapping into Kiota's serialization to trigger the proper conversion.

        // TODO Generate OpenAPI specs for all APIs. This would make it possible to generate SDKs for all APIs,
        // and thus removing the need for the generic `restPost`
        const serializationWriterFactoryRegistry =
          new SerializationWriterFactoryRegistry();

        serializationWriterFactoryRegistry.registerDefaultSerializer(
          JsonSerializationWriterFactory
        );

        const orderDataReSerialized =
          serializationWriterFactoryRegistry.serializeToJsonAsString(
            orderDataTemp,
            serializeWebhusetOrderApiFeaturesCartOrderPrepareExtendedOrderAddRestDto
          );

        const orderData = JSON.parse(orderDataReSerialized);

        const res = await restPost<OrderAddResult>(
          `/customer/${authStore.customerId}/checkout`,
          orderData as Record<string, unknown>
        );

        if (res.checkout_url) {
          await navigateTo(res.checkout_url, {
            external: true,
          });

          // Hack to keep the submit button in a loading state. Hopefully the navigation completed by then.
          await new Promise((resolve) => setTimeout(resolve, 5_000));
          return;
        }

        if (!res.order_id) {
          throw new Error(
            "Failed to place order (both checkout_url and order_id was null)"
          );
        }

        await navigateTo({
          path: "/kvittering",
          query: {
            orderId: res.order_id,
          },
        });
      } catch (err) {
        const statusCode =
          (err as ApiError)?.responseStatusCode ||
          (err as WebhusetApiError)?.errCode;

        if (statusCode && [401, 403].includes(statusCode)) {
          console.log("got 401, logging out");
          await authStore.logout(true);
          return;
        }

        if (statusCode === 442) {
          toast.add({
            id: "cart-place-order-error",
            icon: "i-heroicons-exclamation-triangle",
            title: "En feil oppstod",
            description:
              "Ordren kunne ikke legges til da et eller flere domener i handlekurven allerede er registrert i våre systemer. Vennligst kontakt oss på livechat eller telefon 902 03 000 for videre assistanse.",
            color: "red",
          });
          return;
        }

        if (statusCode === 447 && microsoft365.value.length) {
          toast.add({
            id: "cart-place-order-error",
            icon: "i-heroicons-exclamation-triangle",
            title: "En feil oppstod",
            description:
              "Microsoft 365 kan dessverre kun bestilles av kunder med adresse i Norge. Produktet har derfor blitt fjernet fra handlekurven din.",
            color: "red",
          });

          for (const m365 of microsoft365.value) {
            await removeFromCart(m365.id!);
          }

          return;
        }

        console.warn(err);
        toast.add({
          id: "cart-place-order-error",
          icon: "i-heroicons-exclamation-triangle",
          title: "En feil oppstod",
          description:
            "Kunne ikke registrere ordren akkurat nå. Vennligst forsøk igjen, eller kontakt support dersom feilen vedvarer.",
          color: "red",
        });
      }
    };

    const finalize = async (orderId: number) => {
      if (!cartId.value) {
        console.error("No cart id found, unable to finalize");
        return;
      }

      try {
        await client.cart.byCartId(cartId.value!).finalize.put({
          orderId: orderId,
        });
      } catch (e) {
        console.error("Failed to finalize cart", e);
      } finally {
        cartId.value = undefined;
        items.value = [];
        coupon.value = config.public.defaultCoupon;
        domainSubscriptionPaymentInterval.value =
          WebhusetOrderApiDataDomainSubscriptionPaymentIntervalItemObject.Annually;
        hasAcceptedTerms.value = false;
        hasAcceptedPrivacy.value = false;
        selectedPaymentMethod.value = undefined;
        domainDiscountQuotaExceeded.value = false;
        domainDiscountQuotaExceededDetails.value = undefined;
        hasSeenDomainAddonsPage.value = false;
      }
    };

    return {
      init,
      addDomainToCart,
      addVpsToCart,
      addMicrosoft365ToCart,
      updateMicrosoft365Item,
      addAddOnToCart,
      removeFromCart,
      itemCount,
      items,
      coupon,
      domains: computed(() => items.value.filter((item) => item.domain?.fqdn)),
      microsoft365: microsoft365,
      addons: computed(() => items.value.filter((item) => item.addOn?.addOnId)),
      virtualServers: computed(() =>
        items.value.filter((item) => item.virtualServer?.subscriptionId)
      ),
      placeOrder,
      finalize,
      initialized,
      error,
      setHasSeenDomainAddonsPage: () => (hasSeenDomainAddonsPage.value = true),
      hasSeenDomainAddonsPage,
      trySetCoupon,
      cartId,
      getQuote,
      domainDiscountQuotaExceeded,
      domainDiscountQuotaExceededDetails,
      domainSubscriptionPaymentInterval,
      tryUpdateDomainSubscriptionPaymentInterval,
      selectedPaymentMethod,
      hasAcceptedTerms,
      hasAcceptedPrivacy,
      canSubmit: computed(
        () =>
          !error.value &&
          initialized.value &&
          !domainDiscountQuotaExceeded.value &&
          selectedPaymentMethod.value &&
          hasAcceptedPrivacy.value &&
          hasAcceptedTerms.value &&
          (microsoftCloudAgreementStore.agreementRequired === false ||
            microsoftCloudAgreementStore.hasSignedAgreement)
      ),
    };
  },
  {
    persist: {
      key: "cart",
      paths: ["cartId", "hasSeenDomainAddonsPage"],
    },
  }
);
